import { isEqual } from 'lodash/fp';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Trigger } from 'dashboard/models/trigger';
import { Workflow, WorkflowStatus } from 'dashboard/models/workflow';
import { WorkflowDeployment } from 'dashboard/models/workflow-deployment';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import {
  WorkflowsActions,
  WorkflowsActionType,
  WorkflowsFilterParams,
} from 'dashboard/redux/actions/workflows';
import { ApiModel, PaginationParams } from 'lib/interfaces/api';

export const initialWorkflowsFilterState: WorkflowsFilterParams = {
  triggerTypes: [
    ApiModel.TriggerApi,
    ApiModel.TriggerEvent,
    ApiModel.TriggerRecurring,
    ApiModel.TriggerSchedule,
  ],
  omni: undefined,
  status: [WorkflowStatus.Running],
};

export const initialWorkflowSortState: SortParams = {
  sort: 'updated_at',
  shouldSortDescending: true,
};

export type WorkflowsState = {
  currentPage: number;
  filters: WorkflowsFilterParams;
  pageSize: number;
  paginationParams: PaginationParams;
  sortParams: SortParams;
  triggers: Trigger[];
  workflows: Workflow[];
  workflowVersions: WorkflowVersion[];
  workflowDeployments: WorkflowDeployment[];
};

export const initialWorkflowsState: WorkflowsState = {
  currentPage: 1,
  filters: initialWorkflowsFilterState,
  pageSize: DEFAULT_PAGE_SIZE,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  paginationParams: undefined,
  sortParams: initialWorkflowSortState,
  triggers: [],
  workflows: [],
  workflowVersions: [],
  workflowDeployments: [],
};

export default (state: WorkflowsState = initialWorkflowsState, action: WorkflowsActions) => {
  switch (action.type) {
    case WorkflowsActionType.SetPageSize: {
      return {
        ...state,
        pageSize: action.payload.pageSize,
      };
    }

    case WorkflowsActionType.SetCurrentPage: {
      return {
        ...state,
        currentPage: action.payload.currentPage,
      };
    }

    case WorkflowsActionType.SetPaginationParams: {
      return {
        ...state,
        paginationParams: action.payload.paginationParams,
      };
    }

    case WorkflowsActionType.SetWorkflows: {
      return {
        ...state,
        workflows: action.payload.workflows,
      };
    }

    case WorkflowsActionType.AddWorkflow: {
      return {
        ...state,
        workflows: [
          ...state.workflows.filter((wfl) => wfl.id !== action.payload.workflow.id),
          action.payload.workflow,
        ],
      };
    }

    case WorkflowsActionType.SetWorkflowVersions: {
      return {
        ...state,
        workflowVersions: action.payload.workflowVersions,
      };
    }
    case WorkflowsActionType.SetWorkflowDeployments: {
      return {
        ...state,
        workflowDeployments: action.payload.workflowDeployments,
      };
    }

    case WorkflowsActionType.SetTriggers: {
      return {
        ...state,
        triggers: action.payload.triggers,
      };
    }

    case WorkflowsActionType.SetFilters: {
      const newFilters = {
        ...state.filters,
        ...action.payload.filters,
      };

      if (isEqual(newFilters, state.filters)) {
        return state;
      } else {
        return {
          ...state,
          filters: newFilters,
          // clear workflows
          workflows: [],
        };
      }
    }

    case WorkflowsActionType.SetSort: {
      return {
        ...state,
        sortParams: {
          ...state.sortParams,
          ...action.payload.sortParams,
        },
        // clear workflows
        workflows: [],
      };
    }

    default:
      return state;
  }
};
